import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ContentAuthoringErrorToast,
  ContentAuthoringSuccessToast,
  ContentAuthoringTranslationOutcomeToast,
} from '../Toast/ContentAuthoringOutcomeToasts';
import { authoringCoursesKeys } from '../../hooks/useAuthoringCourses';
import {
  clearCourseTranslationOutcome,
  readCourseTranslationOutcome,
  type CourseTranslationOutcome,
} from './courseTranslationOutcomeState';
import {
  resumeTranslationJobPolling,
  TRANSLATION_OUTCOME_UPDATED_EVENT,
} from './translationJobPollingService';

/** Shows translation job outcome toasts on any authoring page (CEE-1065). */
export function CourseTranslationOutcomeToastHost() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<CourseTranslationOutcome | null>(null);

  const loadOutcome = useCallback(() => {
    const stored = readCourseTranslationOutcome();
    if (stored) {
      setOutcome(stored);
      clearCourseTranslationOutcome();
    }
  }, []);

  useEffect(() => {
    const invalidateCourseDetail = () =>
      queryClient.invalidateQueries({ queryKey: authoringCoursesKeys.all });

    resumeTranslationJobPolling(invalidateCourseDetail);
    loadOutcome();

    const handleOutcomeUpdated = () => {
      loadOutcome();
    };

    window.addEventListener(TRANSLATION_OUTCOME_UPDATED_EVENT, handleOutcomeUpdated);
    return () => {
      window.removeEventListener(TRANSLATION_OUTCOME_UPDATED_EVENT, handleOutcomeUpdated);
    };
  }, [loadOutcome, queryClient]);

  const handleDismiss = useCallback(() => {
    setOutcome(null);
  }, []);

  const handleGoToTranslations = useCallback(() => {
    if (!outcome) {
      return;
    }
    const { courseId } = outcome;
    setOutcome(null);
    navigate(`/authoring/courses/${courseId}/translations`);
  }, [navigate, outcome]);

  const readyMessage =
    outcome?.outcome === 'ready'
      ? t('page.authoring_courses.translations.outcome_toast.ready', {
          name: outcome.languageLabel,
        })
      : '';

  return (
    <>
      <ContentAuthoringSuccessToast
        open={outcome?.outcome === 'ready'}
        message={readyMessage}
        onDismiss={handleDismiss}
        dismissLabel={t('common.dismiss')}
        actionLabel={t('page.authoring_courses.translations.outcome_toast.go_to_translations')}
        onAction={handleGoToTranslations}
      />

      <ContentAuthoringTranslationOutcomeToast
        open={outcome?.outcome === 'partial'}
        severity="warning"
        message={t('page.authoring_courses.translations.outcome_toast.partial')}
        onDismiss={handleDismiss}
        onGoToTranslations={handleGoToTranslations}
      />

      <ContentAuthoringErrorToast
        open={outcome?.outcome === 'failed'}
        message={t('page.authoring_courses.translations.outcome_toast.failed', {
          name: outcome?.languageLabel ?? '',
        })}
        dismissLabel={t('common.dismiss')}
        retryLabel={t('page.authoring_courses.translations.outcome_toast.go_to_translations')}
        onDismiss={handleDismiss}
        onRetry={handleGoToTranslations}
      />
    </>
  );
}
